import { useState } from 'react';
import { IconChecklist, IconFlag } from '@tabler/icons-react';
import { useT } from '@hana/i18n';
import '../edu.i18n';
import { PageHeader, useLayout } from '@hana/shell';
import { Button, Skeleton, StateView } from '@hana/ui';
import { COURSES, type Course } from '../data/courses';
import { PASS_SCORE, bestTry, examState, matches, progress, ring, type ExamState } from '../data/logic';
import { SearchRow } from '../courses/Courses';
import { useEdu } from '../useEdu';
import { useCourseDrawer } from '../courses/CourseDrawer';
import s from '../edu.module.css';

// Màn Thi & đánh giá (board Mint-Training, app `exam`): thẻ bài kiểm tra theo khoá, lọc Tất cả / Sẵn sàng / Đạt / Chưa mở.

type Filter = 'all' | 'ready' | 'pass' | 'lock';
const FILTERS: readonly { k: Filter; label: string }[] = [{ k: 'all', label: 'Tất cả' }, { k: 'ready', label: 'Sẵn sàng' }, { k: 'pass', label: 'Đạt' }, { k: 'lock', label: 'Chưa mở' }];

const LABEL: Record<ExamState, string> = { pass: 'Đạt', ready: 'Sẵn sàng', retry: 'Chưa đạt · làm lại', lock: 'Học xong để mở' };


function ExamCard({ c, onGo }: { c: Course; onGo: (c: Course) => void }) {
  const st = examState(c);
  const best = bestTry(c);
  const text = st === 'pass' ? `Đạt ${best}%` : LABEL[st];
  const p = st === 'lock' ? progress(c) : best / 100;
  const mid = st === 'lock' ? `${c.done}/${c.total}` : `${best}%`;
  const col = st === 'pass' ? 'var(--ok-dot)' : st === 'ready' ? 'var(--pri)' : st === 'retry' ? 'var(--d-amber)' : 'var(--line)';
  const cta = st === 'pass' ? 'Xem lại bài' : st === 'lock' ? `Học tiếp ${c.done}/${c.total}` : `Làm bài · ${c.quiz} câu`;
  return (
    <section className={`${s.card} ${s.exam}`} aria-label={c.short}>
      <div className={s.examTop}>
        <svg role="img" aria-label={`${c.short} · ${text}`} width="60" height="60" viewBox="0 0 48 48">
          <circle cx="24" cy="24" r="18" fill="none" stroke="var(--line)" strokeWidth="6" />
          <circle cx="24" cy="24" r="18" fill="none" stroke={col} strokeWidth="6" strokeLinecap="round" strokeDasharray={ring(p)} transform="rotate(-90 24 24)" />
          <text x="24" y="28" textAnchor="middle" fontSize="11" fontWeight="600" fill="var(--tx)" fontFamily="Inter, sans-serif">{mid}</text>
        </svg>
        <div className={s.examName}>
          <b>{c.short}</b>
          <span className={s.pill} data-tone={st === 'pass' ? 'ok' : st === 'ready' ? 'go' : st === 'retry' ? 'warn' : undefined}>{text}</span>
        </div>
      </div>
      <div className={s.examMeta}>
        <span title="Số câu"><IconChecklist size={14} aria-hidden="true" />{c.quiz}</span>
        <span title="Điểm đạt"><IconFlag size={14} aria-hidden="true" />{PASS_SCORE}%</span>
        <span className={s.tries} title="Các lần làm">
          {c.tries.map((v, i) => <span key={i} title={`${v}%`} style={{ height: Math.max(4, Math.round((v / 100) * 22)), background: v >= PASS_SCORE ? 'var(--ok-dot)' : 'var(--d-amber)' }} />)}
        </span>
      </div>
      <button type="button" className={s.examCta} data-tone={st === 'ready' || st === 'retry' ? 'go' : undefined} disabled={st === 'lock'} onClick={() => onGo(c)}>{cta}</button>
    </section>
  );
}

function ExamsBody({ q, f, open }: { q: string; f: Filter; open: (c: Course) => void }) {
  const t = useT();
  const list = COURSES.filter((c) => matches(c, q) && (f === 'all' || examState(c) === f || (f === 'ready' && examState(c) === 'retry')));
  return (
    <div className={s.exams}>
      {list.map((c) => <ExamCard key={c.id} c={c} onGo={open} />)}
      {!list.length && <div className={s.noHit}>{t('ed.noExam')}</div>}
    </div>
  );
}

export function ExamsScreen() {
  const t = useT();
  const { state } = useEdu();
  const phone = useLayout() === 'phone';
  const cd = useCourseDrawer();
  const [q, setQ] = useState('');
  const [f, setF] = useState<Filter>('all');
  const count = (k: Filter) => COURSES.filter((c) => k === 'all' || examState(c) === k || (k === 'ready' && examState(c) === 'retry')).length;
  return (
    <>
      <PageHeader
        title="Thi & đánh giá"
        actions={state === 'ready' ? <Button variant="primary" onClick={() => cd.open(COURSES[2]!, 'quiz')}>{t('ed.takeQuiz')}</Button> : undefined}
      />
      {state === 'ready' && (
        <div className={s.page}>
          {!phone && <SearchRow placeholder="Tìm bài kiểm tra" q={q} onQ={setQ} sum={`${COURSES.length} bài`} />}
          <div className={s.chips} role="group" aria-label="Lọc bài kiểm tra">
            {FILTERS.map((x) => <button key={x.k} type="button" aria-pressed={f === x.k} onClick={() => setF(x.k)}>{x.label} <span>{count(x.k)}</span></button>)}
          </div>
        </div>
      )}
      <StateView
        state={state}
        title={state === 'empty' ? 'Chưa có khoá học nào được giao' : undefined}
        desc={state === 'empty' ? 'Chưa có khoá bắt buộc.' : undefined}
        skeleton={<div className={s.skel}><Skeleton rows={5} /></div>}
      >
        <div className={s.page}><ExamsBody q={q} f={f} open={(c) => cd.open(c, examState(c) === 'lock' ? 'les' : 'quiz')} /></div>
      </StateView>
      {cd.node}
    </>
  );
}
